import React, { useState, useRef, useEffect, useCallback } from 'react';
import { chatApi } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Robot, PaperPlaneTilt, Sparkle, User, Trash, Plus, Clock
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import logger from '../lib/logger';

export default function AIAssistantPage() {
  const [messages, setMessages] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  const fetchSessions = useCallback(async () => {
    try {
      const response = await chatApi.getSessions();
      setSessions(response.data);
    } catch (error) { logger.error(error); }
  }, []);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const loadSession = async (sessionId) => {
    setCurrentSession(sessionId);
    try {
      const response = await chatApi.getHistory({ session_id: sessionId });
      setMessages(response.data.messages);
    } catch (error) {
      toast.error('Failed to load session');
    }
  };

  const startNewSession = () => {
    setCurrentSession(null);
    setMessages([]);
  };

  const deleteSession = async (sessionId) => {
    try {
      await chatApi.deleteSession(sessionId);
      if (currentSession === sessionId) startNewSession();
      fetchSessions();
      toast.success('Session deleted');
    } catch (error) {
      toast.error('Failed to delete session');
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg, created_at: new Date().toISOString() }]);
    setLoading(true);

    try {
      const response = await chatApi.send(userMsg, currentSession);
      setCurrentSession(response.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response, created_at: new Date().toISOString() }]);
      fetchSessions();
    } catch (error) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.', created_at: new Date().toISOString() }]);
    } finally {
      setLoading(false);
    }
  };

  const SUGGESTIONS = [
    'What are my top performing deals?',
    'Give me a revenue forecast for this quarter',
    'Which sector has the most potential?',
    'How can I improve my pipeline conversion rate?',
    'Suggest strategies to acquire new clients',
    'Analyze my procurement spending',
  ];

  return (
    <div className="flex h-[calc(100vh-5rem)] gap-0" data-testid="ai-assistant-page">
      {/* Sessions Sidebar */}
      <div className="w-72 flex-shrink-0 bg-card border-r border-border flex flex-col">
        <div className="p-4 border-b border-border">
          <Button onClick={startNewSession} className="w-full rounded-sm" data-testid="new-chat-btn">
            <Plus size={16} className="mr-2" />
            New Chat
          </Button>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {sessions.map((session) => (
              <div
                key={session.session_id}
                className={`p-3 rounded-sm cursor-pointer group transition-colors ${
                  currentSession === session.session_id ? 'bg-primary/10 border border-primary/20' : 'hover:bg-muted'
                }`}
                onClick={() => loadSession(session.session_id)}
                data-testid={`session-${session.session_id}`}
              >
                <div className="flex items-start justify-between">
                  <p className="text-sm truncate flex-1 font-medium">{session.last_message}</p>
                  <button
                    className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive ml-2"
                    onClick={(e) => { e.stopPropagation(); deleteSession(session.session_id); }}
                  >
                    <Trash size={14} />
                  </button>
                </div>
                <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                  <Clock size={10} />
                  <span>{session.message_count} messages</span>
                </div>
              </div>
            ))}
            {sessions.length === 0 && (
              <p className="text-center text-sm text-muted-foreground py-8">No conversations yet</p>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center gap-3">
          <div className="h-10 w-10 rounded-sm bg-primary flex items-center justify-center">
            <Sparkle size={22} className="text-primary-foreground" />
          </div>
          <div>
            <h1 className="font-heading text-xl font-bold">BIL AI Assistant</h1>
            <p className="text-xs text-muted-foreground">Business Intelligence copilot powered by GPT-5.2</p>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4" ref={scrollRef}>
          {messages.length === 0 && (
            <div className="max-w-2xl mx-auto pt-12">
              <div className="text-center mb-8">
                <Robot size={48} className="mx-auto text-primary mb-4" />
                <h2 className="font-heading text-2xl font-bold">How can I help you today?</h2>
                <p className="text-muted-foreground mt-2">I have access to your CRM data and can provide business insights, deal analysis, and strategic recommendations.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {SUGGESTIONS.map((q) => (
                  <button
                    key={q}
                    className="text-left p-4 border border-border rounded-sm hover:bg-muted hover:-translate-y-[1px] transition-all"
                    onClick={() => setInput(q)}
                    data-testid={`suggestion-${q.slice(0, 20).replace(/\s/g, '-')}`}
                  >
                    <p className="text-sm font-medium">{q}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`flex gap-3 max-w-3xl ${msg.role === 'user' ? 'ml-auto' : ''}`}>
              {msg.role === 'assistant' && (
                <div className="h-9 w-9 rounded-sm bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Sparkle size={18} className="text-primary" />
                </div>
              )}
              <div className={`flex-1 ${msg.role === 'user' ? 'text-right' : ''}`}>
                <div className={`inline-block p-4 rounded-sm text-sm max-w-full ${
                  msg.role === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card border border-border'
                }`}>
                  <p className="whitespace-pre-wrap text-left">{msg.content}</p>
                </div>
              </div>
              {msg.role === 'user' && (
                <div className="h-9 w-9 rounded-sm bg-muted flex items-center justify-center flex-shrink-0">
                  <User size={18} />
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="flex gap-3 max-w-3xl">
              <div className="h-9 w-9 rounded-sm bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Sparkle size={18} className="text-primary" />
              </div>
              <div className="bg-card border border-border p-4 rounded-sm">
                <div className="flex gap-1.5">
                  <span className="h-2.5 w-2.5 bg-primary/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                  <span className="h-2.5 w-2.5 bg-primary/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                  <span className="h-2.5 w-2.5 bg-primary/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="p-4 border-t border-border">
          <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="flex gap-3 max-w-3xl mx-auto">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about deals, contacts, revenue, strategy..."
              className="flex-1 rounded-sm"
              disabled={loading}
              data-testid="ai-chat-input"
            />
            <Button type="submit" disabled={loading || !input.trim()} className="rounded-sm px-6" data-testid="ai-chat-send">
              <PaperPlaneTilt size={20} />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
